import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { disableBrowserPush } from "../lib/push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password, role) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, role });
      setUser(data);
      // Team members gate the admin UI by access_level; hydrate it from /auth/me
      // if the login payload didn't carry it, so nav renders correctly on first paint.
      if (data?.role === "team_member" && data.access_level == null) {
        try { const me = await api.get("/auth/me"); setUser(me.data); return { ok: true, user: me.data }; } catch { /* keep login data */ }
      }
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await disableBrowserPush(); }
    catch (e) { console.error("Could not disable browser stock alerts:", e?.message); }
    try { await api.post("/auth/logout"); }
    catch (e) { console.error("Logout request failed:", e?.message || e); }
    setUser(false);
  }, []);

  const otpSend = useCallback(async (phone) => {
    try {
      const { data } = await api.post("/auth/otp/send", { phone });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const otpVerify = useCallback(async (phone, otp, name) => {
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, otp, name });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const updateProfile = useCallback(async (payload) => {
    try {
      const { data } = await api.patch("/auth/me", payload);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, otpSend, otpVerify, updateProfile }),
    [user, loading, login, register, logout, refresh, otpSend, otpVerify, updateProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
